import React, { useEffect, useState, useMemo } from "react";
import { Button, Card, CardBody, CardTitle, Col, Row } from "reactstrap";
import "./datatables.scss";

// datatable related plugins
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import ToolkitProvider, { CSVExport, Search } from 'react-bootstrap-table2-toolkit';
import LoaderCard, { TableSkeletonLoader } from "modules/loader";
const { ExportCSVButton } = CSVExport;
const { SearchBar } = Search;

// eslint-disable-next-line react/prop-types
const SizePerPageDropdown = ({
  options = [],
  currSizePerPage,
  onSizePerPageChange,
  rowsLength = 0,
}) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = React.useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const numCurr = parseInt(currSizePerPage, 10);
  const isAll =
    currSizePerPage === "All" ||
    currSizePerPage === "all" ||
    numCurr > 100 ||
    (rowsLength > 0 && numCurr === rowsLength);

  const displayText = isAll ? "All" : (currSizePerPage || "10");

  return (
    <span
      className={`react-bs-table-sizePerPage-dropdown dropdown ${open ? "open show" : ""}`}
      ref={dropdownRef}
    >
      <button
        id="pageDropDown"
        type="button"
        className="btn btn-secondary dropdown-toggle"
        data-toggle="dropdown"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        {displayText} <span className="caret" />
      </button>
      <ul
        className={`dropdown-menu ${open ? "open show" : ""}`}
        role="menu"
        aria-labelledby="pageDropDown"
      >
        {options.map((opt) => {
          const optIsAll = opt.text === "All" || parseInt(opt.page, 10) > 100;
          const isSelected = optIsAll
            ? isAll
            : String(opt.page) === String(currSizePerPage);
          return (
            <li
              key={opt.text}
              role="presentation"
              className="dropdown-item p-0"
            >
              <a
                href="#"
                tabIndex="-1"
                role="menuitem"
                data-page={opt.page}
                className={`dropdown-item ${isSelected ? "active" : ""}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onSizePerPageChange(opt.page);
                  setOpen(false);
                }}
              >
                {opt.text}
              </a>
            </li>
          );
        })}
      </ul>
    </span>
  );
};

// eslint-disable-next-line react/prop-types
const Datatables = ({
  filename = '',
  isSearch = false,
  placeholder = 'Search',
  rowsLength = 0,
  rows,
  handleAddButton,
  title,
  columns,
  loading,
  batchSize = 100,
  batchProgress = null,
  onCancelBatch = null,
  selectField,
  defaultSorted,
  keyField,
  btnTitle = 'Add',
  isAdd = true,
  isTableHead = true,
  isDisabled = false,
  fas = false,
  localSearch = false,
  is_remote = true,
  csv = true,
  pdf = false,
  excel = false,
  isExport = false,
  isPagination = true,
  btnColor = 'primary',
  titleClass = 'h2',
  onExport = null,
  rowEvents = {},
  ssr = () => { } }
) => {

  const _csv = isExport || csv;
  const _excel = isExport || excel;
  const _pdf = isExport || pdf;

  const [page, setPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState(''); // State for the search input
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm); // State for the debounced input

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const isFirstMountRef = React.useRef(true);
  useEffect(() => {
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
      return;
    }
    const isAll = sizePerPage === (rowsLength || 100000) || sizePerPage === 'all' || sizePerPage === 'All' || (typeof sizePerPage === 'number' && sizePerPage > 100);
    setPage(1);
    ssr({ page: 1, limit: isAll ? 'all' : sizePerPage, isAll, search: debouncedSearchTerm });
  }, [debouncedSearchTerm]);

  const memoizedData = useMemo(() => {
    let list = [];
    if (Array.isArray(rows)) {
      list = rows;
    } else if (rows && typeof rows === 'object') {
      if (Array.isArray(rows.rows)) {
        list = rows.rows;
      } else if (rows.rows && typeof rows.rows === 'object') {
        list = Object.values(rows.rows);
      } else {
        list = Object.values(rows);
      }
    }
    return list
      .filter(item => item !== null && item !== undefined && typeof item === 'object')
      .map((item, idx) => {
        if (keyField && (item[keyField] === undefined || item[keyField] === null)) {
          return { ...item, [keyField]: item.id ?? item._id ?? item.order_number ?? `row-${idx}` };
        }
        return item;
      });
  }, [rows, keyField]);

  const memoizedColumns = useMemo(() => {
    if (!columns) return [];
    const currentPage = parseInt(page, 10) || 1;
    const currentLimit = (sizePerPage === 'All' || sizePerPage === 'all' || sizePerPage === rowsLength || sizePerPage > 100) ? 0 : (parseInt(sizePerPage, 10) || 10);

    return columns.map((col) => {
      const isIndexCol = col.dataField === 'id' || col.dataField === 'sr_no' || col.dataField === 'sno' || col.dataField === 'index' || col.dataField === '#' || col.text === '#' || col.text === 'Sr No' || col.text === 'Sr.No' || col.text === 'S.No' || col.text === 'S. No';

      if (isIndexCol) {
        return {
          ...col,
          formatter: (cell, row, rowIndex, formatExtraData) => {
            const continuousIndex = currentLimit === 0 ? (rowIndex + 1) : (((currentPage - 1) * currentLimit) + (rowIndex + 1));
            if (col.formatter) {
              const res = col.formatter(cell, row, rowIndex, { ...formatExtraData, page: currentPage, sizePerPage: currentLimit || rowsLength, continuousIndex });
              // If the custom formatter simply calculated rowIndex + 1 (resulting in 1..limit on page > 1), fix it to continuousIndex
              if (typeof res === 'number' && currentPage > 1 && currentLimit > 0 && res <= currentLimit) {
                return continuousIndex;
              }
              return res;
            }
            if (col.text === '#' || col.text === 'Sr No' || col.text === 'Sr.No' || col.text === 'S.No' || col.text === 'S. No' || col.text === 'Id' || col.text === 'ID') {
              return continuousIndex;
            }
            return cell !== undefined && cell !== null ? cell : continuousIndex;
          }
        };
      }
      return col;
    });
  }, [columns, page, sizePerPage, rowsLength]);

  const sizePerPageList = useMemo(() => [
    { text: '10', value: 10 },
    { text: '25', value: 25 },
    { text: '50', value: 50 },
    { text: '100', value: 100 },
    { text: 'All', value: rowsLength > 0 ? rowsLength : 100000 }
  ], [rowsLength]);

  const renderSizePerPage = React.useCallback(
    ({ options, currSizePerPage, onSizePerPageChange }) => (
      <SizePerPageDropdown
        options={options}
        currSizePerPage={currSizePerPage}
        onSizePerPageChange={onSizePerPageChange}
        rowsLength={rowsLength}
      />
    ),
    [rowsLength]
  );

  const pageOptions = {
    sizePerPage: sizePerPage,
    totalSize: rowsLength,
    page: page || 1,
    hideSizePerPage: false,
    sizePerPageList: sizePerPageList,
    sizePerPageRenderer: renderSizePerPage,
  };

  const prevPageRef = React.useRef(page);
  const prevSizePerPageRef = React.useRef(sizePerPage);

  const handleTableChange = (type, { page: newPage, sizePerPage: newSizePerPage }) => {
    if (type !== 'pagination') return;
    if (newPage === prevPageRef.current && newSizePerPage === prevSizePerPageRef.current) {
      return;
    }
    prevPageRef.current = newPage;
    prevSizePerPageRef.current = newSizePerPage;

    const isAll = newSizePerPage === (rowsLength || 100000) || newSizePerPage === 'all' || newSizePerPage === 'All' || (typeof newSizePerPage === 'number' && newSizePerPage > 100);
    setPage(newPage);
    setSizePerPage(newSizePerPage);
    if (typeof ssr === 'function') {
      ssr({ page: newPage, limit: isAll ? 'all' : newSizePerPage, isAll, search: searchTerm });
    }
  };

  const exportPDF = () => {
    import("jspdf").then((jsPDF) => {
      import("html2canvas").then((html2canvas) => {
        const input = document.querySelector(".table-responsive");
        html2canvas.default(input).then((canvas) => {
          const imgData = canvas.toDataURL("image/png");
          const pdfDoc = new jsPDF.jsPDF("p", "mm", "a4");
          const imgProps = pdfDoc.getImageProperties(imgData);
          const pdfWidth = pdfDoc.internal.pageSize.getWidth();
          const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
          pdfDoc.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
          pdfDoc.save(`${filename || 'report'}.pdf`);
        });
      });
    });
  };

  const handleExportCSV = () => {
    if (!memoizedData || memoizedData.length === 0) return;

    const columnsToExport = memoizedColumns.filter(c => c.dataField !== 'action' && c.dataField !== 'actions' && c.dataField !== 'id' && c.csvExport !== false);
    const headers = columnsToExport.map(col => col.text).join(',');

    const body = memoizedData.map(row => {
      return columnsToExport.map(col => {
        let val = col.csvFormatter ? col.csvFormatter(row[col.dataField], row) : row[col.dataField];
        if (val === null || val === undefined) val = '';
        return `"${val.toString().replace(/"/g, '""')}"`;
      }).join(',');
    }).join('\n');

    // Check if any column has a footer defined
    const hasFooter = columnsToExport.some(col => col.footer !== undefined && col.footer !== '');
    let footerRowContent = '';
    if (hasFooter) {
      footerRowContent = '\n' + columnsToExport.map(col => {
        let val = col.footer;
        if (val === undefined || val === null) val = '';
        if (typeof val === 'function') {
          val = val();
        }
        return `"${val.toString().replace(/"/g, '""')}"`;
      }).join(',');
    }

    const csvContent = headers + '\n' + body + footerRowContent;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    const exportName = (filename || title || 'export').toLowerCase().replace(/\s+/g, '_');
    link.setAttribute("download", `${exportName}_${new Date().toISOString().slice(0, 10)}_${new Date().getTime()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <React.Fragment>
      <Row>
        <Col className="col-12">
          <ToolkitProvider
            key={`${keyField || 'table'}_${memoizedData.length}_${page}`}
            keyField={keyField || 'id'}
            data={memoizedData}
            columns={memoizedColumns}
            search
            exportCSV={{
              fileName: `${filename || 'export'}_${new Date().toISOString().slice(0, 10)}_${new Date().getTime()}.csv`
            }}
          >
            {props => (
              <>
                {
                  isTableHead ?
                    <div className="datatable-header-wrapper d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                      <div className="datatable-header-title d-flex align-items-center flex-wrap gap-2">
                        {title && <CardTitle className={`${titleClass} mb-0`}>{title}</CardTitle>}
                        {selectField}
                      </div>

                      <div className="datatable-header-actions d-flex align-items-center flex-wrap gap-2 ms-auto">
                        {
                          isSearch ?
                            <div className="search-box">
                              <div className="position-relative">
                                <input className="form-control" placeholder={placeholder} onChange={(e) => {
                                  setSearchTerm(e.target.value)
                                }} defaultValue={searchTerm} />
                                <i className="bx bx-search-alt search-icon" />
                              </div>
                            </div> : ''
                        }

                        {
                          localSearch ? <div className="search-box"> <SearchBar {...props.searchProps} placeholder="Search clients..." clearSearch /></div> : ''
                        }

                        {
                          _csv ? <Button color="success" size="sm" onClick={onExport ? () => onExport('csv') : handleExportCSV}><i className="fas fa-file-csv me-1" /> CSV</Button> : ''
                        }
                        {
                          _excel ? <Button color="info" size="sm" onClick={onExport ? () => onExport('excel') : handleExportCSV}><i className="fas fa-file-excel me-1" /> Excel</Button> : ''
                        }
                        {
                          _pdf ? <Button color="danger" size="sm" onClick={exportPDF}><i className="fas fa-file-pdf me-1" /> PDF</Button> : ''
                        }
                        {
                          isAdd ?
                            <Button
                              disabled={isDisabled}
                              color={btnColor}
                              className="datatable-add-btn text-white waves-effect waves-light"
                              onClick={handleAddButton}
                              onKeyDown={handleAddButton}
                            >
                              <i className={`fas ${fas ? fas : "fa-plus"} me-1`}></i>{" "}{btnTitle}
                            </Button> : ''
                        }
                      </div>
                    </div> : ''
                }

                {batchProgress && (batchProgress.active || batchProgress.completed || batchProgress.stopped) && (
                  <div
                    className={`alert ${batchProgress.active ? 'alert-info' : batchProgress.stopped ? 'alert-warning' : 'alert-success'} d-flex align-items-center justify-content-between p-3 mb-3 shadow-sm rounded fade-in-content`}
                    style={{ borderLeft: '4px solid #42210b' }}
                  >
                    <div className="d-flex align-items-center flex-grow-1 me-3">
                      {batchProgress.active ? (
                        <div className="spinner-border spinner-border-sm text-primary me-3" role="status" style={{ minWidth: '1rem', minHeight: '1rem' }} />
                      ) : batchProgress.stopped ? (
                        <i className="fas fa-pause-circle text-warning me-3 font-size-18" />
                      ) : (
                        <i className="fas fa-check-circle text-success me-3 font-size-18" />
                      )}
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="fw-semibold font-size-13 text-dark">
                            {batchProgress.active ? (
                              <>
                                Loading all records in batches of {batchSize || 100} rows:{" "}
                                <strong className="text-primary">{batchProgress.loaded?.toLocaleString()} / {batchProgress.total?.toLocaleString()} loaded</strong>
                              </>
                            ) : batchProgress.stopped ? (
                              <>
                                Batch loading stopped:{" "}
                                <strong>{batchProgress.loaded?.toLocaleString()} / {batchProgress.total?.toLocaleString()} rows loaded</strong>
                              </>
                            ) : (
                              <>
                                All records loaded successfully:{" "}
                                <strong className="text-success">{batchProgress.loaded?.toLocaleString()} / {batchProgress.total?.toLocaleString()} rows</strong>
                              </>
                            )}
                          </span>
                          <span className="badge bg-primary font-size-12 px-2 py-1">
                            {batchProgress.percent !== undefined
                              ? `${batchProgress.percent}%`
                              : `${Math.min(100, Math.round((batchProgress.loaded / (batchProgress.total || 1)) * 100))}%`}
                          </span>
                        </div>
                        {batchProgress.active && (
                          <div className="progress" style={{ height: '6px', backgroundColor: '#e9ecef' }}>
                            <div
                              className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                              role="progressbar"
                              style={{
                                width: `${batchProgress.percent !== undefined
                                  ? batchProgress.percent
                                  : Math.min(100, Math.round((batchProgress.loaded / (batchProgress.total || 1)) * 100))}%`
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    {batchProgress.active && onCancelBatch && (
                      <Button color="secondary" size="sm" outline onClick={onCancelBatch} title="Stop loading further batches">
                        <i className="fas fa-stop me-1" /> Stop Loading
                      </Button>
                    )}
                  </div>
                )}

                <div className="mt-3">
                  {loading ? (
                    <TableSkeletonLoader rows={sizePerPage || 5} columns={columns?.length || 5} />
                  ) :
                    memoizedData.length === 0 ? (
                      <div className="text-center py-5 text-muted fade-in-content">
                        <i className="bx bx-folder-open font-size-24 d-block mb-2 text-secondary" />
                        <span>No records found</span>
                      </div>
                    ) : (
                      <div className="fade-in-content">
                        <BootstrapTable
                          {...props.baseProps}
                          classes="table align-middle table-nowrap table-hover mb-0"
                          wrapperClasses="table-responsive"
                          headerWrapperClasses="thead-custom"
                          remote={is_remote}
                          sort={defaultSorted}
                          pagination={isPagination ? paginationFactory(pageOptions) : undefined}
                          onTableChange={handleTableChange}
                          rowEvents={rowEvents}
                        />
                      </div>
                    )
                  }
                </div>
              </>
            )}
          </ToolkitProvider>
        </Col>
      </Row>
    </React.Fragment>
  )
}

export default Datatables
